import axios from 'axios';

const SERVER = (process.env.REACT_APP_SERVER || '').replace(/\/$/, '');
const AUTH_KEY = 'garam-user-auth';
let refreshRequest;

function validSession(data) {
    return typeof data?.accessToken === 'string' && data.accessToken.trim()
        && data.user && typeof data.user === 'object' && !Array.isArray(data.user);
}

export function readUserSession() {
    try {
        const data = JSON.parse(sessionStorage.getItem(AUTH_KEY));
        return validSession(data) ? data : null;
    } catch {
        return null;
    }
}

export function clearUserSession() {
    sessionStorage.removeItem(AUTH_KEY);
    window.dispatchEvent(new Event('garam-auth-change'));
}

// 회원 정보 수정 후 저장된 세션의 user만 교체한다. 토큰은 그대로 둔다.
export function updateSessionUser(user) {
    const session = readUserSession();
    if (!session) return;
    sessionStorage.setItem(AUTH_KEY, JSON.stringify({ ...session, user }));
    window.dispatchEvent(new Event('garam-auth-change'));
}

export function refreshUserSession() {
    // StrictMode에서도 refresh 쿠키를 동시에 두 번 갱신하지 않는다.
    if (!refreshRequest) {
        refreshRequest = axios.post(`${SERVER}/api/auth/refresh`, {}, { withCredentials: true, timeout: 45000 })
            .then(({ data }) => {
                if (!validSession(data)) throw new Error('로그인 응답을 확인하지 못했습니다.');
                const session = { accessToken: data.accessToken, user: data.user };
                sessionStorage.setItem(AUTH_KEY, JSON.stringify(session));
                window.dispatchEvent(new Event('garam-auth-change'));
                return session;
            })
            .catch(error => {
                clearUserSession();
                throw error;
            })
            .finally(() => { refreshRequest = undefined; });
    }
    return refreshRequest;
}

// 서명 검증은 서버가 한다. 프론트는 만료 여부만 보고 재발급 시점을 정한다.
export function isAccessTokenExpired(token, skewSeconds = 30) {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const { exp } = JSON.parse(atob(base64));
        return typeof exp !== 'number' || exp * 1000 - skewSeconds * 1000 <= Date.now();
    } catch {
        return true;
    }
}

// 유효한 세션을 돌려주고, 없거나 만료됐으면 refresh 쿠키로 재발급한다. 실패하면 null.
export async function ensureUserSession() {
    const session = readUserSession();
    if (session && !isAccessTokenExpired(session.accessToken)) return session;
    try {
        return await refreshUserSession();
    } catch {
        return null;
    }
}

// 로그인이 필요한 API 호출용. 토큰을 붙이고, 401이면 한 번 재발급 후 재시도한다.
export const authApi = axios.create({ baseURL: SERVER, withCredentials: true, timeout: 45000 });

authApi.interceptors.request.use(async config => {
    const session = await ensureUserSession();
    if (!session) throw new axios.Cancel('LOGIN_REQUIRED');
    config.headers.Authorization = `Bearer ${session.accessToken}`;
    return config;
});

authApi.interceptors.response.use(undefined, async error => {
    const config = error.config;
    if (error.response?.status !== 401 || !config || config._retried) {
        if (error.response?.status === 401) clearUserSession();
        throw error;
    }
    config._retried = true;
    try {
        const session = await refreshUserSession();
        config.headers.Authorization = `Bearer ${session.accessToken}`;
        return authApi.request(config);
    } catch {
        throw error;
    }
});

export async function logoutUserSession() {
    await axios.post(`${SERVER}/api/auth/logout`, {}, { withCredentials: true, timeout: 45000 });
    clearUserSession();
}
